import { alpha } from '@mantine/core';
import { Alert } from 'src/design/atoms/Alert';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import React from 'react';
import { formatRate } from 'src/utils';
import { IRateBreakdownInstructions } from 'src/app/timesheets/types';
import { EffectiveRateBreakdown } from 'src/app/timesheets/EffectiveRateBreakdown';
import { DisclosurePopover } from 'src/design/molecules/DisclosurePopover';

export interface IEffectiveRateCellProps {
  effectiveRate: number | null | undefined;
  instructions: IRateBreakdownInstructions | null | undefined;
  loading: boolean;
  error: string | null;
  isActualized?: boolean;
  payCodeColor?: string;
}

/** @registry Timesheet rate cell showing the formatted effective rate with a keyboard-accessible breakdown popover; handles loading/error/N-A states. */
export const EffectiveRateCell: React.FC<IEffectiveRateCellProps> = ({
  effectiveRate,
  instructions,
  loading,
  error,
  isActualized = false,
  payCodeColor,
}) => {
  if (loading) {
    return (
      <Text ta="center" size="sm" c="dimmed">
        <Loader size="xs" />
      </Text>
    );
  }

  if (error) {
    return (
      <Text ta="center" size="sm" c="red">
        Error
      </Text>
    );
  }

  if (effectiveRate == null || !instructions) {
    return (
      <Text ta="center" size="sm" c="dimmed">
        N/A
      </Text>
    );
  }

  return (
    <DisclosurePopover
      label={`Rate breakdown for ${formatRate(effectiveRate)}`}
      width={500}
      position="bottom"
      content={
        <Stack gap="sm">
          {!isActualized && (
            <Alert color="blue">
              <Text size="xs">
                <Text component="span" fw={600} c="red">
                  *
                </Text>{' '}
                This rate is subject to change. The final rate will be locked-in
                when the timesheet is approved.
              </Text>
            </Alert>
          )}
          <EffectiveRateBreakdown instructions={instructions} />
        </Stack>
      }
    >
      <UnstyledButton
        type="button"
        style={{
          cursor: 'help',
          textDecoration: 'underline',
          textDecorationStyle: 'dotted',
          // 0.125 ~= 0x20/0xFF, matching the prior hard-coded hex-alpha appearance
          // while accepting any colour format alpha() understands (hex/rgb/named).
          backgroundColor: payCodeColor
            ? alpha(payCodeColor, 0.125)
            : undefined,
          borderRadius: 4,
          padding: '2px 4px',
          display: 'block',
          width: '100%',
          textAlign: 'center',
          font: 'inherit',
        }}
      >
        <Text component="span" ta="center" size="sm" c="bright">
          {formatRate(effectiveRate)}
          {!isActualized && (
            <Text
              component="span"
              c="red"
              ml={4} // We want a smaller size than XS
            >
              *
            </Text>
          )}
        </Text>
      </UnstyledButton>
    </DisclosurePopover>
  );
};
